import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Truck, Award, Clock, AlertTriangle, Euro, Target } from 'lucide-react';
import { KpiCard } from '../KpiCard';
type Carrier = {
  name: string;
  shipments: number;
  onTimeRate: number;
  cost: number;
  avgTime: number;
  stockRate: number;
  delayRate: number;
  trend: number;
};
const CARRIERS: Carrier[] = [{
  name: 'FedEx',
  shipments: 1680,
  onTimeRate: 96.8,
  cost: 12.6,
  avgTime: 1.1,
  stockRate: 0.9,
  delayRate: 1.4,
  trend: 2.4
}, {
  name: 'BRT',
  shipments: 920,
  onTimeRate: 94.2,
  cost: 8.4,
  avgTime: 1.4,
  stockRate: 1.6,
  delayRate: 2.1,
  trend: 1.8
}, {
  name: 'GLS',
  shipments: 680,
  onTimeRate: 93.1,
  cost: 9.2,
  avgTime: 1.6,
  stockRate: 2.0,
  delayRate: 2.7,
  trend: 0.9
}, {
  name: 'UPS',
  shipments: 320,
  onTimeRate: 90.4,
  cost: 11.4,
  avgTime: 1.3,
  stockRate: 2.4,
  delayRate: 3.2,
  trend: -0.6
}, {
  name: 'SDA',
  shipments: 242,
  onTimeRate: 87.2,
  cost: 7.9,
  avgTime: 2.1,
  stockRate: 3.8,
  delayRate: 4.6,
  trend: -1.2
}];
const LOGOS: Record<string, string> = {
  BRT: 'https://cdn.magicpatterns.com/uploads/cmtJEf49a5eVZLpdSNFSih/BRT_logo_cropped.png',
  FedEx: 'https://cdn.magicpatterns.com/uploads/bJFTTvq6RiT6tx6MWd19DM/FedEx-Logo.png',
  GLS: 'https://cdn.magicpatterns.com/uploads/kLsfczSQhw6HQ17cu2iXe1/GLS_Logo_2021.svg.png',
  SDA: 'https://cdn.magicpatterns.com/uploads/uzuZLxLhuWymtodu3YDZcY/1620120317-sda-poste-logo.webp',
  UPS: 'https://cdn.magicpatterns.com/uploads/5fMcthEgdAwTbqPzFRcjsj/logo_corriere_ups_500.png'
};
type Lane = {
  city: string;
  bestCarrier: string;
  deliveryRate: number;
  avgTime: number;
};
const LANES: Lane[] = [{
  city: 'Milano',
  bestCarrier: 'FedEx',
  deliveryRate: 98.9,
  avgTime: 1.2
}, {
  city: 'Roma',
  bestCarrier: 'FedEx',
  deliveryRate: 99.1,
  avgTime: 0.9
}, {
  city: 'Firenze',
  bestCarrier: 'FedEx',
  deliveryRate: 98.4,
  avgTime: 1.3
}, {
  city: 'Torino',
  bestCarrier: 'FedEx',
  deliveryRate: 98.0,
  avgTime: 1.4
}, {
  city: 'Bologna',
  bestCarrier: 'FedEx',
  deliveryRate: 98.9,
  avgTime: 1.2
}, {
  city: 'Napoli',
  bestCarrier: 'GLS',
  deliveryRate: 95.4,
  avgTime: 1.8
}, {
  city: 'Bari',
  bestCarrier: 'FedEx',
  deliveryRate: 96.2,
  avgTime: 1.7
}, {
  city: 'Palermo',
  bestCarrier: 'BRT',
  deliveryRate: 94.8,
  avgTime: 2.4
}, {
  city: 'Catania',
  bestCarrier: 'GLS',
  deliveryRate: 92.1,
  avgTime: 2.6
}];
type Metric = 'onTimeRate' | 'cost' | 'avgTime' | 'stockRate' | 'delayRate';
const METRICS: {
  key: Metric;
  label: string;
  suffix: string;
  goodHigh: boolean;
}[] = [{
  key: 'onTimeRate',
  label: 'Puntualità',
  suffix: '%',
  goodHigh: true
}, {
  key: 'cost',
  label: 'Costo / spedizione',
  suffix: ' €',
  goodHigh: false
}, {
  key: 'avgTime',
  label: 'Tempo medio',
  suffix: ' g',
  goodHigh: false
}, {
  key: 'stockRate',
  label: '% giacenze',
  suffix: '%',
  goodHigh: false
}, {
  key: 'delayRate',
  label: '% ritardi',
  suffix: '%',
  goodHigh: false
}];
export function CorrieriPerformance() {
  const totalShipments = CARRIERS.reduce((s, c) => s + c.shipments, 0);
  const avgOnTime = CARRIERS.reduce((s, c) => s + c.onTimeRate * c.shipments, 0) / totalShipments;
  const avgCost = CARRIERS.reduce((s, c) => s + c.cost * c.shipments, 0) / totalShipments;
  const avgTime = CARRIERS.reduce((s, c) => s + c.avgTime * c.shipments, 0) / totalShipments;
  const avgStock = CARRIERS.reduce((s, c) => s + c.stockRate * c.shipments, 0) / totalShipments;
  const avgDelay = CARRIERS.reduce((s, c) => s + c.delayRate * c.shipments, 0) / totalShipments;
  return <div className="space-y-6" data-id="element-1739">
      <h1 className="text-2xl font-semibold text-[#FAFAFA] font-heading tracking-tight" data-id="element-1740">
        Performance
      </h1>

      {/* KPI strip */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-id="element-1741">
        <KpiCard label="Costo medio" value={`€ ${avgCost.toFixed(2)}`} data-id="element-1742" />
        <KpiCard label="Puntualità" value={`${avgOnTime.toFixed(1)}%`} data-id="element-1743" />
        <KpiCard label="Tempo medio" value={`${avgTime.toFixed(1)} g`} data-id="element-1744" />
        <KpiCard label="% giacenze" value={`${avgStock.toFixed(1)}%`} data-id="element-1745" />
        <KpiCard label="% ritardi" value={`${avgDelay.toFixed(1)}%`} data-id="element-1746" />
      </section>

      {/* Comparison chart (Heatmap) */}

      {/* Detailed table */}
      <section className="bg-[#181818] border border-[#262626] rounded-xl" data-id="element-1747">
        <div className="p-5 border-b border-[#262626] flex items-center justify-between gap-3 flex-wrap" data-id="element-1748">
          <div data-id="element-1749">
            <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-1750">
              Dettaglio corrieri
            </h3>
            <p className="text-xs text-[#6B6B6B] mt-0.5" data-id="element-1751">
              Ultimi 30 giorni — {totalShipments.toLocaleString('it-IT')}{' '}
              spedizioni
            </p>
          </div>
        </div>

        <div className="hidden md:block overflow-x-auto" data-id="element-1752">
          <table className="w-full text-sm [&_th]:whitespace-nowrap [&_td]:whitespace-nowrap" data-id="element-1753">
            <thead data-id="element-1754">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B6B6B] border-b border-[#1F1F1F]" data-id="element-1755">
                <th className="px-5 py-3 font-medium" data-id="element-1756">Corriere</th>
                <th className="px-5 py-3 font-medium" data-id="element-1757">Spedizioni</th>
                <th className="px-5 py-3 font-medium" data-id="element-1758">Puntualità</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1759">
                  Costo / sped.
                </th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1760">
                  Tempo medio
                </th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1761">% giacenze</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1762">% ritardi</th>
                <th className="px-5 py-3 font-medium text-right" data-id="element-1763">Trend 30g</th>
              </tr>
            </thead>
            <tbody data-id="element-1764">
              {CARRIERS.map((c, idx) => {
              const isTop = idx === 0;
              const trendUp = c.trend >= 0;
              const TrendIcon = trendUp ? TrendingUp : TrendingDown;
              return <tr key={c.name} className="border-b border-[#1F1F1F] last:border-0 hover:bg-[#0A0A0A] transition-colors" data-id="element-1765">
                    <td className="px-5 py-4" data-id="element-1766">
                      <div className="flex items-center gap-3" data-id="element-1767">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0 p-1.5" data-id="element-1768">
                          <img src={LOGOS[c.name]} alt={c.name} className="max-w-full max-h-full object-contain" data-id="element-1769" />
                        </div>
                        <div data-id="element-1770">
                          <div className="text-[#FAFAFA] font-medium flex items-center gap-2" data-id="element-1771">
                            {c.name}
                            {isTop && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#D4FF3A] text-[#0A0A0A] font-semibold" data-id="element-1772">
                                <Award className="h-2.5 w-2.5" data-id="element-1773" />
                                TOP
                              </span>}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-1774">
                      <div className="flex items-center gap-2" data-id="element-1775">
                        <div className="w-20 h-1 bg-[#0A0A0A] rounded-full overflow-hidden" data-id="element-1776">
                          <div className="h-full rounded-full bg-[#D4FF38]" style={{
                        width: `${c.shipments / CARRIERS[0].shipments * 100}%`
                      }} data-id="element-1777" />
                        </div>
                        <span className="text-xs font-mono text-[#9A9A9A]" data-id="element-1778">
                          {c.shipments.toLocaleString('it-IT')}
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-4" data-id="element-1779">
                      <PercentageBar value={c.onTimeRate} data-id="element-1780" />
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-sm text-[#FAFAFA]" data-id="element-1781">
                      € {c.cost.toFixed(2)}
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-sm text-[#9A9A9A]" data-id="element-1782">
                      {c.avgTime}g
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-1783">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono ${c.stockRate > 3 ? 'bg-[#EF4444]/10 text-[#EF4444]' : c.stockRate > 2 ? 'bg-[#F5A524]/10 text-[#F5A524]' : 'bg-[#1F1F1F] text-[#9A9A9A]'}`} data-id="element-1784">
                        {c.stockRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right" data-id="element-1785">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono ${c.delayRate > 3.5 ? 'bg-[#EF4444]/10 text-[#EF4444]' : c.delayRate > 2.5 ? 'bg-[#F5A524]/10 text-[#F5A524]' : 'bg-[#1F1F1F] text-[#9A9A9A]'}`} data-id="element-1786">
                        {c.delayRate.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-5 py-4" data-id="element-1787">
                      <div className={`flex items-center justify-end gap-1 text-xs font-medium ${trendUp ? 'text-[#D4FF3A]' : 'text-[#EF4444]'}`} data-id="element-1788">
                        <TrendIcon className="h-3.5 w-3.5" data-id="element-1789" />
                        <span className="font-mono" data-id="element-1790">
                          {trendUp ? '+' : ''}
                          {c.trend.toFixed(1)}pp
                        </span>
                      </div>
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {/* Mobile carrier cards */}
        <div className="md:hidden divide-y divide-[#1F1F1F]" data-id="element-1791">
          {CARRIERS.map((c, idx) => {
          const isTop = idx === 0;
          const trendUp = c.trend >= 0;
          const TrendIcon = trendUp ? TrendingUp : TrendingDown;
          return <div key={c.name} className="p-4 space-y-3" data-id="element-1792">
                <div className="flex items-center justify-between gap-3" data-id="element-1793">
                  <div className="flex items-center gap-3 min-w-0" data-id="element-1794">
                    <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shrink-0 p-1.5" data-id="element-1795">
                      <img src={LOGOS[c.name]} alt={c.name} className="max-w-full max-h-full object-contain" data-id="element-1796" />
                    </div>
                    <div className="text-[#FAFAFA] font-medium flex items-center gap-2" data-id="element-1797">
                      {c.name}
                      {isTop && <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded bg-[#D4FF3A] text-[#0A0A0A] font-semibold" data-id="element-1798">
                          <Award className="h-2.5 w-2.5" data-id="element-1799" />
                          TOP
                        </span>}
                    </div>
                  </div>
                  <div className={`flex items-center gap-1 text-xs font-medium ${trendUp ? 'text-[#D4FF3A]' : 'text-[#EF4444]'}`} data-id="element-1800">
                    <TrendIcon className="h-3.5 w-3.5" data-id="element-1801" />
                    <span className="font-mono" data-id="element-1802">
                      {trendUp ? '+' : ''}
                      {c.trend.toFixed(1)}pp
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-3" data-id="element-1803">
                  <div data-id="element-1804">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1.5" data-id="element-1805">
                      Puntualità
                    </div>
                    <PercentageBar value={c.onTimeRate} data-id="element-1806" />
                  </div>
                  <div data-id="element-1807">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium mb-1.5" data-id="element-1808">
                      Tempo medio
                    </div>
                    <div className="text-sm font-mono text-[#FAFAFA] mt-0.5" data-id="element-1809">
                      {c.avgTime}g
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-3 pt-1" data-id="element-1810">
                  <div data-id="element-1811">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1812">
                      Volume
                    </div>
                    <div className="text-sm font-mono text-[#FAFAFA] mt-0.5" data-id="element-1813">
                      {c.shipments.toLocaleString('it-IT')}
                    </div>
                  </div>
                  <div data-id="element-1814">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1815">
                      Costo
                    </div>
                    <div className="text-sm font-mono text-[#FAFAFA] mt-0.5" data-id="element-1816">
                      € {c.cost.toFixed(2)}
                    </div>
                  </div>
                  <div data-id="element-1817">
                    <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-medium" data-id="element-1818">
                      % giacenze
                    </div>
                    <div className="mt-0.5" data-id="element-1819">
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-mono ${c.stockRate > 3 ? 'bg-[#EF4444]/10 text-[#EF4444]' : c.stockRate > 2 ? 'bg-[#F5A524]/10 text-[#F5A524]' : 'bg-[#1F1F1F] text-[#9A9A9A]'}`} data-id="element-1820">
                        {c.stockRate.toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>;
        })}
        </div>
      </section>

      {/* Best carrier per lane */}
      <section className="bg-[#181818] border border-[#262626] rounded-xl" data-id="element-1821">
        <div className="p-5 border-b border-[#262626]" data-id="element-1822">
          <h3 className="text-base font-semibold text-[#FAFAFA] font-heading" data-id="element-1823">
            Miglior corriere per tratta
          </h3>
          <p className="text-xs text-[#6B6B6B] mt-0.5" data-id="element-1824">
            Roma → negozio, basato su tasso di consegna degli ultimi 30 giorni
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1F1F1F]" data-id="element-1825">
          {LANES.map(lane => <div key={lane.city} className="bg-[#181818] p-4 flex items-center gap-3 hover:bg-[#1a1a1a] transition-colors" data-id="element-1826">
              <div className="h-9 w-10 rounded bg-white flex items-center justify-center shrink-0 p-1.5" data-id="element-1827">
                <img src={LOGOS[lane.bestCarrier]} alt={lane.bestCarrier} className="max-w-full max-h-full object-contain" data-id="element-1828" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1829">
                <div className="flex items-center justify-between gap-2 mb-0.5" data-id="element-1830">
                  <span className="text-sm font-medium text-[#FAFAFA] truncate" data-id="element-1831">
                    {lane.city}
                  </span>
                  <span className="text-xs font-mono text-[#D4FF3A]" data-id="element-1832">
                    {lane.deliveryRate.toFixed(1)}%
                  </span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-[#6B6B6B]" data-id="element-1833">
                  <span className="text-[#9A9A9A]" data-id="element-1834">{lane.bestCarrier}</span>
                  <span data-id="element-1835">•</span>
                  <span className="font-mono" data-id="element-1836">{lane.avgTime} g</span>
                </div>
              </div>
            </div>)}
        </div>
      </section>
    </div>;
}
function PercentageBar({
  value
}: {
  value: number;
}) {
  const color = value >= 96 ? '#D4FF3A' : value >= 93 ? '#F5A524' : '#EF4444';
  return <div className="flex items-center gap-2" data-id="element-1837">
      <div className="w-24 h-1 bg-[#0A0A0A] rounded-full overflow-hidden" data-id="element-1838">
        <div className="h-full rounded-full" style={{
        width: `${value}%`,
        background: color
      }} data-id="element-1839" />
      </div>
      <span className="text-xs font-mono text-[#FAFAFA] w-12" data-id="element-1840">
        {value.toFixed(1)}%
      </span>
    </div>;
}
function StatCard({
  label,
  value,
  icon,
  accent
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
  accent?: boolean;
}) {
  return <div className={`bg-[#181818] border rounded-xl p-5 ${accent ? 'border-[#D4FF3A]/30' : 'border-[#262626]'}`} data-id="element-1841">
      <div className="flex items-center justify-between mb-3" data-id="element-1842">
        <span className="text-xs text-[#9A9A9A] font-medium" data-id="element-1843">{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-semibold font-heading tracking-tight text-[#FAFAFA]" data-id="element-1844">
        {value}
      </div>
    </div>;
}